import React, { useState } from "react";
import {
  Table,
  Text,
  Group,
  Stack,
  Button,
  Box,
  ActionIcon,
  Avatar,
  Badge,
  ScrollArea,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconEye,
  IconMail,
  IconClock,
  IconUsers,
  IconUser,
  IconCurrencyDollar,
} from "@tabler/icons-react";

const formatCurrency = value => {
  if (value === null || value === undefined || value === "") return "—";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
};

const formatPercentage = value => {
  if (value === null || value === undefined || value === "") return "—";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "—";
  const formatted =
    numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toFixed(1);
  return formatted + "%";
};

const formatDate = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const mod10 = day % 10;
  const mod100 = day % 100;
  const suffix =
    mod10 === 1 && mod100 !== 11
      ? "st"
      : mod10 === 2 && mod100 !== 12
        ? "nd"
        : mod10 === 3 && mod100 !== 13
          ? "rd"
          : "th";
  const month = d.toLocaleDateString("en-US", { month: "long" });
  return `${month} ${day}${suffix}`;
};

const getLastSeen = rep => {
  if (!rep.last_active_time) return "Never";
  const lastActiveDate = new Date(rep.last_active_time);
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const lastActiveStart = new Date(
    lastActiveDate.getFullYear(),
    lastActiveDate.getMonth(),
    lastActiveDate.getDate()
  );
  const diffDays = Math.floor(
    (todayStart - lastActiveStart) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

const REP_TYPE_COLORS = {
  veteran: "violet",
  rookie: "orange",
  senior: "blue",
  manager: "teal",
};

const getRepTypeColor = name => {
  if (!name) return "gray";
  return REP_TYPE_COLORS[name.toLowerCase()] || "blue";
};

const getRepProfileImage = rep =>
  rep.profile_image_url || rep.profile_image || rep.avatar_url || undefined;

const getRepInitials = rep =>
  `${rep.first_name?.charAt(0)?.toUpperCase() || ""}${rep.last_name?.charAt(0)?.toUpperCase() || ""}` ||
  rep.rep_name
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(name => name.charAt(0).toUpperCase())
    .join("") ||
  "R";

const TREE_LINE_COLOR = "#ced4da";

// Avatar size used in the Name column
const AVATAR_SIZE = 60;
// Left padding applied to every td in the Name column (overrides Mantine default)
const CELL_LEFT_PADDING = 16;
// Per-level indent.  Must be > AVATAR_SIZE (60) so there is a visible
// horizontal gap between the right edge of the parent avatar and the left
// edge of the child row.  80 px gives a 20 px connector span per level.
const CONNECTOR_WIDTH = 80; // px

// X position of the HORIZONTAL CENTRE of the avatar at depth i.
// All branch lines (vertical pass-through, horizontal elbow, downward stub)
// are anchored here so they align with the avatar's visual centre.
const avatarCenterX = i =>
  i * CONNECTOR_WIDTH + CELL_LEFT_PADDING + AVATAR_SIZE / 2;

/**
 * Returns CSS background-* styles that draw tree-connector lines painted
 * DIRECTLY on the <td> element so they span its full height (including
 * padding) and never have gaps between rows.
 *
 * lineX at depth i = avatarCenterX(i)
 * This aligns the vertical line with the horizontal centre of the Avatar
 * rendered in a row at that depth.
 *
 * ancestorIsLast[i] = true  →  ancestor at depth i was the last sibling,
 * no continuing vertical line for that column.
 */
const buildTreeLineBackground = (level, isLastInGroup, ancestorIsLast) => {
  if (level === 0) return {};

  const images = [];
  const sizes = [];
  const positions = [];

  // Horizontal branch: from center of parent avatar → left edge of this avatar
  // Stops at the circle boundary so it doesn't visually run through the avatar.
  const hStartX = avatarCenterX(level - 1);
  const hEndX = avatarCenterX(level) - AVATAR_SIZE / 2; // left edge of child
  const hWidth = hEndX - hStartX; // = CONNECTOR_WIDTH - AVATAR_SIZE/2 = 50 px
  images.push(`linear-gradient(${TREE_LINE_COLOR}, ${TREE_LINE_COLOR})`);
  sizes.push(`${hWidth}px 2px`);
  positions.push(`${hStartX}px 50%`);

  // Vertical lines for each depth 0..level-1
  for (let i = 0; i < level; i++) {
    const isInnermost = i === level - 1;

    // Never draw the root (level-0) column line on rows deeper than level 1.
    // That prevents the top-level manager's vertical line from bleeding through
    // into nested sub-trees.
    if (i === 0 && level >= 2) continue;

    // Non-innermost ancestors: skip the column line only when the *next-level*
    // ancestor (index i+1) was last in its group — meaning there are no more
    // siblings at depth i+1 below this row and no line is needed at column i.
    // Using ancestorIsLast[i] was wrong: it checked whether ancestor i was the
    // last sibling of ITS parent, which is irrelevant for rows inside its own
    // sub-tree (e.g. Susie being last under Fillmore should not suppress her
    // column line inside her own expanded children/grandchildren).
    if (!isInnermost && ancestorIsLast[i + 1]) continue;

    const continueVertical = isInnermost ? !isLastInGroup : true;
    const lineX = avatarCenterX(i) - 1;

    images.push(`linear-gradient(${TREE_LINE_COLOR}, ${TREE_LINE_COLOR})`);
    sizes.push(`2px ${continueVertical ? "100%" : "50%"}`);
    positions.push(`${lineX}px 0`);
  }

  return {
    backgroundImage: images.join(", "),
    backgroundSize: sizes.join(", "),
    backgroundPosition: positions.join(", "),
    backgroundRepeat: "no-repeat",
  };
};

/**
 * When a parent row is expanded we need to draw a downward connector from the
 * avatar's bottom edge to the cell bottom.  We CANNOT do this with a CSS
 * background gradient on the <td> because the Avatar uses radius="xl" which
 * clips it to a circle — the transparent corners of the avatar's bounding box
 * let the background show straight through the middle of the avatar.
 *
 * Instead we only return the ancestor tree lines (same as a normal row) and
 * render a real DOM element for the downward stub in the JSX below.
 */
const buildExpandedParentBackground = (
  level,
  isLastInGroup,
  ancestorIsLast
) => {
  // Ancestor lines only — no downward extension here.
  return buildTreeLineBackground(level, isLastInGroup, ancestorIsLast);
};

const RepRows = ({
  reps,
  level = 0,
  ancestorIsLast = [],
  showPayDetails,
  onResendInvite,
  onViewDetails,
  onViewPayDetails,
  onPitchReview,
  resendingInvite,
}) => {
  const [expandedIds, setExpandedIds] = useState({});

  const toggleExpand = id => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const rows = [];

  reps.forEach((rep, index) => {
    const hasSubReps = rep.reps && rep.reps.length > 0;
    const isExpanded = !!expandedIds[rep.rep_id];
    const isLastInGroup = index === reps.length - 1;

    const isManagerWithTeam =
      rep.is_manager &&
      (rep.team_projected_revenue !== undefined ||
        rep.team_projected_margin !== undefined);
    const isRepManagerWithTeamEarnings =
      rep.is_manager && rep.rep_team_earnings !== undefined;

    const repTypeName = rep.rep_type?.name || rep.role_name || null;

    // Expanded parent rows get a downward extension line from their avatar;
    // all other rows just get the ancestor connector lines.
    const treeLineBg =
      hasSubReps && isExpanded
        ? buildExpandedParentBackground(level, isLastInGroup, ancestorIsLast)
        : buildTreeLineBackground(level, isLastInGroup, ancestorIsLast);

    rows.push(
      <Table.Tr key={rep.rep_id}>
        {/* Name — tree lines are CSS backgrounds on this td.
            The downward connector for expanded parent rows is anchored inside
            a position:relative wrapper div around the Avatar — this is the
            only reliable containing block (td positioning is inconsistent
            across browsers with Mantine's table styles). */}
        <Table.Td
          style={{
            ...treeLineBg,
            paddingLeft: level * CONNECTOR_WIDTH + CELL_LEFT_PADDING,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Downward connector for expanded parents — anchored directly to
              the <td> (position:relative) so overflow:hidden clips it cleanly
              at the exact cell boundary with no sub-pixel gaps.
              top:"50%" aligns with the avatar's vertical centre; the solid
              circular avatar hides the line where it overlaps, so only the
              lower half (avatar-centre → cell-bottom) is visible. */}
          {hasSubReps && isExpanded && (
            <div
              style={{
                position: "absolute",
                // Start at the BOTTOM EDGE of the avatar circle:
                // cell-midpoint (50%) + avatar-radius (AVATAR_SIZE/2)
                top: `calc(50% + ${AVATAR_SIZE / 2}px)`,
                left: avatarCenterX(level) - 1,
                width: 2,
                // Fill from avatar bottom to cell bottom
                height: `calc(50% - ${AVATAR_SIZE / 2}px)`,
                background: TREE_LINE_COLOR,
                pointerEvents: "none",
              }}
            />
          )}
          <Group gap="sm" wrap="nowrap">
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                src={getRepProfileImage(rep)}
                size={AVATAR_SIZE}
                radius="xl"
                color="orange"
              >
                {getRepInitials(rep)}
              </Avatar>
            </div>
            <Stack gap={4}>
              <Group gap="xs" align="center">
                <Text fz="lg" fw={400}>
                  {rep.first_name} {rep.last_name}
                </Text>
                {repTypeName && (
                  <Badge
                    variant="light"
                    color={getRepTypeColor(repTypeName)}
                    size="sm"
                    radius="sm"
                  >
                    {repTypeName}
                  </Badge>
                )}
              </Group>
              <Group gap={4} align="center">
                <IconClock size={15} color="gray" />
                <Text size="sm" c="dimmed">
                  {getLastSeen(rep)}
                </Text>
              </Group>
            </Stack>
          </Group>
        </Table.Td>

        {/* Team Size */}
        <Table.Td>
          <Group gap="sm" align="center" wrap="nowrap">
            <Avatar
              size={44}
              radius="xl"
              color={rep.rep_count > 0 ? "blue" : "gray"}
              variant="light"
            >
              <Text fz="md" fw={400}>
                {rep.rep_count || 0}
              </Text>
            </Avatar>
            <Stack gap={0}>
              {rep.rep_count > 0 ? (
                <>
                  <Text fz="md" fw={400}>
                    Reps
                  </Text>
                  <Text size="xs" c="dimmed">
                    Recruited
                  </Text>
                </>
              ) : (
                <>
                  <Text fz="md" fw={400}>
                    No recruits
                  </Text>
                  <Text size="xs" c="dimmed">
                    Yet
                  </Text>
                </>
              )}
            </Stack>
          </Group>
        </Table.Td>

        {/* Revenue */}
        <Table.Td>
          <Stack gap={8}>
            {isManagerWithTeam && (
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <Avatar size={44} radius="xl" color="teal" variant="light">
                  <IconUsers size={22} />
                </Avatar>
                <Stack gap={0}>
                  <Text size="sm" c="dimmed" fw={400}>
                    Team
                  </Text>
                  <Text fz="lg" fw={400}>
                    {formatCurrency(rep.team_projected_revenue)}
                  </Text>
                </Stack>
              </Group>
            )}
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Avatar size={44} radius="xl" color="blue" variant="light">
                <IconUser size={22} />
              </Avatar>
              <Stack gap={0}>
                <Text size="sm" c="dimmed" fw={400}>
                  Personal
                </Text>
                <Text fz="lg" fw={400}>
                  {formatCurrency(rep.projected_revenue)}
                </Text>
              </Stack>
            </Group>
          </Stack>
        </Table.Td>

        {showPayDetails ? (
          <>
            {/* Margin */}
            <Table.Td>
              <Stack gap={6}>
                {isManagerWithTeam && (
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed" fw={400}>
                      Team
                    </Text>
                    <Text fz="lg" fw={400}>
                      {formatPercentage(rep.team_projected_margin)}
                    </Text>
                  </Stack>
                )}
                <Stack gap={0}>
                  <Text size="sm" c="dimmed" fw={400}>
                    Personal
                  </Text>
                  <Text fz="lg" fw={400}>
                    {formatPercentage(rep.projected_margin)}
                  </Text>
                </Stack>
              </Stack>
            </Table.Td>
            {/* Earnings */}
            <Table.Td>
              <Stack gap={6}>
                {isManagerWithTeam && (
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed" fw={400}>
                      Team
                    </Text>
                    <Text fz="lg" fw={400}>
                      {formatCurrency(rep.team_projected_earnings)}
                    </Text>
                  </Stack>
                )}
                <Stack gap={0}>
                  <Text size="sm" c="dimmed" fw={400}>
                    Personal
                  </Text>
                  <Text fz="lg" fw={400}>
                    {formatCurrency(rep.projected_earnings)}
                  </Text>
                </Stack>
              </Stack>
            </Table.Td>

            {/* Rep Earnings */}
            <Table.Td>
              <Stack gap={6}>
                {isRepManagerWithTeamEarnings && (
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed" fw={400}>
                      Team
                    </Text>
                    <Text fz="lg" fw={400}>
                      {formatCurrency(rep.rep_team_earnings)} - (
                      {formatPercentage(rep.rep_team_margin_percent)})
                    </Text>
                  </Stack>
                )}
                <Stack gap={0}>
                  <Text size="sm" c="dimmed" fw={400}>
                    Personal
                  </Text>
                  <Text fz="lg" fw={400}>
                    {formatCurrency(rep.rep_commission_earnings)} - (
                    {formatPercentage(rep.rep_commission_percent)})
                  </Text>
                </Stack>
              </Stack>
            </Table.Td>
          </>
        ) : (
          <Table.Td>
            <Text fz="lg" fw={400}>
              {formatDate(rep.start_date)}
            </Text>
          </Table.Td>
        )}

        {/* Actions */}
        <Table.Td style={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
          <Group
            gap="xs"
            wrap="nowrap"
            justify="flex-end"
            align="center"
            style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}
          >
            {!rep.verified && onResendInvite && (
              <Button
                variant="outline"
                color="orange"
                size="xs"
                leftSection={<IconMail size={14} />}
                onClick={() => onResendInvite(rep.rep_id)}
                disabled={resendingInvite === rep.rep_id}
                loading={resendingInvite === rep.rep_id}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                {resendingInvite === rep.rep_id
                  ? "Sending..."
                  : "Resend Invite"}
              </Button>
            )}
            {onViewDetails && (
              <Button
                variant="outline"
                color="orange"
                size="xs"
                leftSection={<IconEye size={14} />}
                onClick={() => onViewDetails(rep.rep_id)}
                style={{
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                View Details
              </Button>
            )}
            {onViewPayDetails && (
              <Button
                variant="outline"
                color="orange"
                size="xs"
                leftSection={<IconCurrencyDollar size={14} />}
                onClick={() => onViewPayDetails(rep.rep_id)}
                style={{
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                View Pay Details
              </Button>
            )}
            {hasSubReps && (
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => toggleExpand(rep.rep_id)}
                style={{ alignSelf: "center" }}
              >
                {isExpanded ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
              </ActionIcon>
            )}
            {!hasSubReps && (
              <Box
                style={{
                  width: 22,
                  height: 22,
                  visibility: "hidden",
                  flexShrink: 0,
                }}
              />
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    );

    if (hasSubReps && isExpanded) {
      rows.push(
        <RepRows
          key={`sub-${rep.rep_id}`}
          reps={rep.reps}
          level={level + 1}
          ancestorIsLast={[...ancestorIsLast, isLastInGroup]}
          showPayDetails={showPayDetails}
          onResendInvite={onResendInvite}
          onViewDetails={onViewDetails}
          onViewPayDetails={onViewPayDetails}
          onPitchReview={onPitchReview}
          resendingInvite={resendingInvite}
        />
      );
    }
  });

  return rows;
};

const RepCardList = ({
  reps,
  showPayDetails = false,
  onResendInvite,
  onViewDetails,
  onViewPayDetails,
  onPitchReview,
  resendingInvite,
}) => {
  const headerCellStyle = {
    background: "#fafbfc",
    borderTop: "1px solid #e5e7eb",
  };
  const headerFirstCellStyle = {
    ...headerCellStyle,
  };
  const headerMiddleCellStyle = headerCellStyle;
  const headerLastCellStyle = {
    ...headerCellStyle,
  };

  if (!reps || reps.length === 0) {
    return (
      <Box ta="center" py="xl">
        <Text c="dimmed">No representatives found.</Text>
      </Box>
    );
  }

  return (
    <ScrollArea>
      <Table
        highlightOnHover
        verticalSpacing="md"
        horizontalSpacing="lg"
        stickyHeader
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={headerFirstCellStyle}>Name</Table.Th>
            <Table.Th style={headerMiddleCellStyle}>Team Size</Table.Th>
            <Table.Th style={headerMiddleCellStyle}>Projected Revenue</Table.Th>
            {showPayDetails ? (
              <>
                <Table.Th style={headerMiddleCellStyle}>
                  Projected Margin
                </Table.Th>
                <Table.Th style={headerMiddleCellStyle}>
                  Projected Earnings
                </Table.Th>
                <Table.Th style={headerMiddleCellStyle}>
                  Projected Rep Earnings
                </Table.Th>
              </>
            ) : (
              <Table.Th style={headerMiddleCellStyle}>Start Date</Table.Th>
            )}
            <Table.Th
              style={{ ...headerLastCellStyle, paddingLeft: 65 }}
              ta="center"
            >
              Actions
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <RepRows
            reps={reps}
            level={0}
            ancestorIsLast={[]}
            showPayDetails={showPayDetails}
            onResendInvite={onResendInvite}
            onViewDetails={onViewDetails}
            onViewPayDetails={onViewPayDetails}
            onPitchReview={onPitchReview}
            resendingInvite={resendingInvite}
          />
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
};

export default RepCardList;
